import {
  chatAttachmentFailure,
  type ChatAttachmentFailure,
  type OntologyAgentEvent
} from '@atbash-labs/ontology-agent-wire'

import { createOntologyHeaders } from '../api/core'
import { getOntologyServiceBaseUrl } from '../config'
import { trackUsageAction } from '../telemetry'
import {
  appendTabTurn,
  bumpAgentOrchestratorConversationToTop,
  cancelTabRun,
  enqueueTabMessage,
  failTabRun,
  finishTabRun,
  isTabRunLive,
  openTab,
  removeTabQueuedMessage,
  removeTabStagedAttachments,
  setPendingDraft,
  setPendingComposerMode,
  setPendingEffortSelection,
  setPendingModelSelection,
  setTabDraft,
  setTabComposerMode,
  setTabEffortSelection,
  setTabModelSelection,
  setTabQueuedMessages,
  setTabStagedAttachmentStatus,
  startTabRun,
  STAGED_ATTACHMENT_SEND_ERROR,
  type OrchestratorEffortSelection,
  type OrchestratorModelSelection,
  updateTabQueuedMessage
} from '../slices/agent-orchestrator-slice'
import {
  releaseStagedAttachments,
  resolveStagedAttachmentFiles
} from './staged-attachment-registry'
import type { AppDispatch, RootState } from '../store'
import {
  AgentConversationRequestError,
  createAgentConversation,
  loadActiveAgentConversation,
  loadActiveAgentConversationAttachments,
  loadAgentConversations
} from './agent-conversations'
import { effortForOrchestratorRequest } from './conversation-effort'
import { hydratePendingCypherWrites } from './cypher-write-pending'
import {
  dispatchUnresumedAgentActionPlans,
  fireSettledAgentActionPlans
} from './cypher-write-resume'
import { createOrchestratorEventHandler } from './orchestrator-event-router'
import { consumeSSE, SSERequestError } from './sse-client'

export { effortForOrchestratorRequest } from './conversation-effort'

/**
 * Per-conversation registry of in-flight SSE consumers, keyed by
 * `conversationId`. Multi-tab support (AXI-3530) replaces the previous
 * singleton AbortController — two tabs can stream simultaneously, and the
 * user can Stop one without affecting the other.
 *
 * Lifecycle:
 *   - `runOntologyAgentOrchestrator` / `resumeOntologyAgentOrchestrator`
 *     register a fresh controller under the conversation id on start.
 *   - `abortOntologyAgentOrchestrator(conversationId)` looks the controller
 *     up and aborts it (plus POSTs `/cancel` to the server).
 *   - `disconnectLocalOrchestratorStream(conversationId)` aborts the local
 *     fetch WITHOUT calling the server (used by close-tab — decision #7
 *     in the AXI-3530 plan).
 *   - The thunk's `finally` block clears the entry when the SSE consumer
 *     resolves / throws.
 */
const abortControllersByConversation = new Map<string, AbortController>()

/**
 * Browser-side ordering companion to the server's Redis FIFO. Requests are
 * opened immediately so every message is staged server-side, but events for a
 * queued turn are buffered until the preceding local turn finishes updating
 * the tab.
 */
const clientTurnTails = new Map<string, Promise<void>>()

/**
 * Message ids that must not appear in the Queued rail — either because this
 * browser already activated them into a live run, or because they were
 * submitted while no prior turn was live (so the optimistic path skips the
 * queue UI). Prevents the 1s Redis poll from flashing a just-sent message
 * during the server's enqueue-before-claim window.
 */
const suppressQueuedUiMessageIds = new Set<string>()

/**
 * Register a fresh controller for a conversation. If an old controller is
 * still around (shouldn't happen — the server-side registry serializes per
 * conversationId — but defensive against a client-side race), abort the
 * old one first so we don't leak the prior fetch.
 */
function registerAbortController(conversationId: string, controller: AbortController): void {
  const existing = abortControllersByConversation.get(conversationId)
  if (existing !== undefined && existing !== controller) {
    existing.abort()
  }
  abortControllersByConversation.set(conversationId, controller)
}

/**
 * Clear a conversation's controller iff it's still the same one we
 * registered (don't stomp a newer controller a concurrent re-submit may
 * have installed).
 */
function clearAbortController(conversationId: string, controller: AbortController): void {
  if (abortControllersByConversation.get(conversationId) === controller) {
    abortControllersByConversation.delete(conversationId)
  }
}

/**
 * Tear down the local SSE consumer for `conversationId` WITHOUT POSTing
 * `/agent-orchestrator/cancel`. Used by close-tab so the server-side
 * orchestrator keeps running in the background (decision #7 in the plan).
 * Re-opening the same conversation via /resume picks the live row back up.
 */
export function disconnectLocalOrchestratorStream(conversationId: string): void {
  const controller = abortControllersByConversation.get(conversationId)
  if (controller === undefined) return
  controller.abort()
  abortControllersByConversation.delete(conversationId)
}

/**
 * Stop the in-flight orchestrator run for a specific conversation
 * (explicit user action — the Stop button). POSTs to the server's cancel
 * endpoint so the orchestrator's AbortSignal fires and the run is
 * finalized as `aborted`, then closes the local fetch so the SSE
 * consumer's catch path settles the run and keeps its partial work.
 *
 * Conversation id is now an explicit argument — the slice no longer
 * tracks a singleton "which stream is in flight" field, so the caller
 * must specify which tab's stream to stop. Today's only call site is the
 * composer's Stop button, which targets `activeTabId`.
 */
export function abortOntologyAgentOrchestrator(conversationId: string) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const controller = abortControllersByConversation.get(conversationId)
    const tab = getState().agentOrchestrator.tabs[conversationId]
    const run = tab?.run ?? null

    // No local SSE consumer — nothing will observe the abort, so settle the
    // run here. A failed run keeps `tab.run` for its error banner; a Stop
    // click must not clear that out from under the user.
    if (controller === undefined) {
      if (run === null) return
      if (run.streamError != null) return
      dispatch(finishTabRun({ id: conversationId }))
      return
    }

    // Flip the cancelling state BEFORE the round trip so the Stop button
    // immediately renders "Cancelling…" + disabled. The stop / fail /
    // finish reducers clear the flag once the SSE consumer catches up.
    // `cancelTabRun` itself no-ops when the run has already settled.
    if (run !== null) {
      dispatch(cancelTabRun({ id: conversationId }))
    }

    try {
      await fetch(`${getOntologyServiceBaseUrl()}/agent-orchestrator/cancel`, {
        method: 'POST',
        headers: createOntologyHeaders(),
        body: JSON.stringify({ conversation_id: conversationId })
      })
    } catch (err) {
      console.warn('[agent-orchestrator] cancel POST failed', err)
    }

    controller.abort()
    clearAbortController(conversationId, controller)
  }
}

/**
 * Build the multipart payload the api-service's orchestrator route
 * accepts when the user attaches files. Field names match the JSON shape
 * (`instruction`, `conversation_id`); files are sent under repeated `file`
 * fields so the existing `readMultipart` helper picks them up.
 */
/** Wire shape for the orchestrator `app_context` field (AXI-4791). */
export interface AppContextEntry {
  key: string
  value: string
}

/** Orchestrator product edition sent on the request (`full` default | `core`). */
export type OrchestratorProductMode = 'full' | 'core'

// The Role view is persisted by the main domain under this localStorage key
// (`operator-view-flag.ts`). Read it directly rather than importing across the
// state → main boundary; a Core view sends `product_mode: 'core'` so the server
// trims the planner catalog + query palette.
const OPERATOR_VIEW_STORAGE_KEY = 'ontology-operator-view'

function readProductMode(): OrchestratorProductMode {
  try {
    const storage = (globalThis as { localStorage?: Storage }).localStorage
    return storage?.getItem(OPERATOR_VIEW_STORAGE_KEY) === 'core' ? 'core' : 'full'
  } catch {
    return 'full'
  }
}

/** Browser-local IANA timezone sent as ambient session context, never as a tool argument. */
export function readBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

function buildOrchestratorFormData(
  instruction: string,
  conversationId: string,
  attachments: File[],
  attachmentClientIds: readonly string[],
  modelSelection: OrchestratorModelSelection | null,
  effortSelection: OrchestratorEffortSelection | null,
  messageId: string,
  mode?: OrchestratorRunMode,
  graphWrite?: boolean,
  appContext?: AppContextEntry[],
  ingestQueryJobId?: string,
  productMode?: OrchestratorProductMode,
  debugMode?: boolean,
  userTimezone = 'UTC'
): FormData {
  const form = new FormData()
  form.set('instruction', instruction)
  form.set('conversation_id', conversationId)
  form.set('message_id', messageId)
  form.set('user_timezone', userTimezone)
  if (productMode === 'core') {
    form.set('product_mode', productMode)
  }
  if (debugMode === true) {
    form.set('debug_mode', 'true')
  }
  if (mode === 'query_agent_only' || mode === 'ingest_query_agent_only') {
    form.set('mode', mode)
  }
  if (mode === 'ingest_query_agent_only' && ingestQueryJobId) {
    form.set('job_id', ingestQueryJobId)
  }
  if (graphWrite === true) {
    form.set('capabilities', JSON.stringify({ graph_write: true }))
  }
  if (modelSelection !== null) {
    form.set('model_provider', modelSelection.provider)
    form.set('model_id', modelSelection.model)
  }
  const effort = effortForOrchestratorRequest(modelSelection, effortSelection)
  if (effort !== null) {
    form.set('effort', effort)
  }
  if (appContext !== undefined && appContext.length > 0) {
    form.set('app_context', JSON.stringify(appContext))
  }
  for (const [index, file] of attachments.entries()) {
    form.append(`file:${attachmentClientIds[index]}`, file, file.name)
  }
  return form
}

function isAttachmentPersistenceFailure(failure: ChatAttachmentFailure): boolean {
  return (
    failure.code === 'ATTACHMENT_STORAGE_UNAVAILABLE' ||
    failure.code === 'ATTACHMENT_UPLOAD_TIMEOUT' ||
    failure.code === 'ATTACHMENT_AUTHORIZATION_FAILED' ||
    failure.code === 'ATTACHMENT_OBJECT_WRITE_FAILED' ||
    failure.code === 'ATTACHMENT_METADATA_WRITE_FAILED' ||
    failure.code === 'ATTACHMENT_IDEMPOTENCY_CONFLICT' ||
    failure.code === 'ATTACHMENT_UPLOAD_IN_PROGRESS' ||
    failure.code === 'ATTACHMENT_DRAFT_EXPIRED' ||
    failure.code === 'ATTACHMENT_CLAIM_FAILED'
  )
}

/**
 * `query_agent_only` and `ingest_query_agent_only` both skip the planner and
 * dispatch a single agent directly. Default `full`.
 */
export type OrchestratorRunMode = 'full' | 'query_agent_only' | 'ingest_query_agent_only'

export interface RunAgentOrchestratorArgs {
  instruction: string
  /**
   * Continue an existing conversation. Omit / pass `null` to let the
   * thunk pre-create one. With multi-tab the controller hook always
   * passes the active tab's id when continuing, or `null` when submitting
   * from the pending new-chat state.
   */
  conversationId?: string | null
  /**
   * Files the user dragged into / picked from the chat input. When
   * non-empty the request is sent as multipart/form-data and the
   * orchestrator's `ingest_agent` becomes a routable subagent for
   * ingest-style prompts. Absent / empty for plain text turns.
   */
  attachments?: File[]
  /**
   * Ordered client IDs matching `attachments`. When present, the thunk
   * accepts or fails exactly this ID set on the target conversation —
   * never the currently focused tab.
   */
  attachmentClientIds?: string[]
  mode?: OrchestratorRunMode
  /**
   * Give a direct query-agent / ingest-query-agent run the propose
   * graph-write rail. Independent from `mode`; omitted/false keeps it
   * read-only.
   */
  graphWrite?: boolean
  /**
   * Required when `mode` is `'ingest_query_agent_only'` — the ingest job
   * this dispatch answers questions about.
   */
  ingestQueryJobId?: string
  /**
   * Ordered UI context snapshot captured at send time (AXI-4791). Sent as
   * `app_context` on the JSON body, or as a JSON-string field on multipart.
   */
  appContext?: AppContextEntry[]
  /** Effective Advanced → Debug mode state captured by the UI at send time. */
  debugMode?: boolean
}

export function loadQueuedOrchestratorMessages(conversationId: string) {
  return async (dispatch: AppDispatch): Promise<void> => {
    const response = await fetch(
      `${getOntologyServiceBaseUrl()}/agent-orchestrator/${encodeURIComponent(
        conversationId
      )}/queued-messages`,
      { headers: createOntologyHeaders() }
    )
    if (!response.ok) {
      throw new AgentConversationRequestError(
        response.status,
        `HTTP ${response.status}: failed to load queued messages`
      )
    }
    const body = (await response.json()) as {
      messages?: Array<{
        id?: unknown
        instruction?: unknown
        queued_at?: unknown
      }>
    }
    const messages = (body.messages ?? []).flatMap((message) =>
      typeof message.id === 'string' &&
      typeof message.instruction === 'string' &&
      typeof message.queued_at === 'string' &&
      !suppressQueuedUiMessageIds.has(message.id)
        ? [
            {
              id: message.id,
              instruction: message.instruction,
              queued_at: message.queued_at
            }
          ]
        : []
    )
    dispatch(setTabQueuedMessages({ id: conversationId, messages }))
  }
}

export function editQueuedOrchestratorMessage(
  conversationId: string,
  messageId: string,
  instruction: string
) {
  return async (dispatch: AppDispatch): Promise<void> => {
    const response = await fetch(
      `${getOntologyServiceBaseUrl()}/agent-orchestrator/${encodeURIComponent(
        conversationId
      )}/queued-messages/${encodeURIComponent(messageId)}`,
      {
        method: 'PATCH',
        headers: createOntologyHeaders(),
        body: JSON.stringify({ instruction })
      }
    )
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: failed to edit queued message`)
    }
    const message = (await response.json()) as {
      id: string
      instruction: string
      queued_at: string
    }
    dispatch(updateTabQueuedMessage({ id: conversationId, message }))
  }
}

export function deleteQueuedOrchestratorMessage(conversationId: string, messageId: string) {
  return async (dispatch: AppDispatch): Promise<void> => {
    const headers = createOntologyHeaders()
    headers.delete('Content-Type')
    const response = await fetch(
      `${getOntologyServiceBaseUrl()}/agent-orchestrator/${encodeURIComponent(
        conversationId
      )}/queued-messages/${encodeURIComponent(messageId)}`,
      { method: 'DELETE', headers }
    )
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: failed to delete queued message`)
    }
    dispatch(removeTabQueuedMessage({ id: conversationId, messageId }))
  }
}

/**
 * Restore the typed prompt to its tab (or the pending new-chat composer)
 * after a failed / stopped run so the user can edit + resubmit without
 * retyping. Picks the right slot based on whether the run was for a tab
 * that still exists; if the tab was closed mid-stream (decision #7 — the
 * server keeps running but the local UI is gone), the dispatch is a
 * silent no-op via the slice's defensive guard.
 */
function restoreDraft(
  dispatch: AppDispatch,
  conversationId: string | null,
  instruction: string
): void {
  if (conversationId === null) {
    dispatch(setPendingDraft(instruction))
    return
  }
  dispatch(setTabDraft({ id: conversationId, draft: instruction }))
}

/**
 * Run the orchestrator for the active conversation. Multi-tab semantics
 * (AXI-3530): never aborts other in-flight runs. Multiple concurrent
 * invocations against different conversations coexist; each registers
 * its own AbortController in `abortControllersByConversation` and routes
 * SSE events to its own tab via the closure in `createEventHandler`.
 *
 * For a fresh conversation (no `conversationId` supplied) the thunk
 * pre-creates the row via `createAgentConversation` (which opens the tab
 * idempotently), then dispatches `openTab` again here is unnecessary —
 * the pre-create already did it. For a continuing conversation, the
 * caller has already ensured the tab is open via `openTab` (typically
 * from the controller hook's handle-select-from-history path).
 */
export function runOntologyAgentOrchestrator(args: RunAgentOrchestratorArgs | string) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const normalized: RunAgentOrchestratorArgs =
      typeof args === 'string' ? { instruction: args } : args
    const trimmed = normalized.instruction.trim()
    if (!trimmed) return

    // Pre-create the conversation if the caller didn't supply one. The
    // controller hook calls this path when the user submitted from the
    // pending new-chat tab (activeTabId === null); `createAgentConversation`
    // both inserts the proxy row and dispatches `openTab` so the tab slot
    // exists by the time we register the abort controller. We DO NOT
    // auto-abort other in-flight runs here — multi-tab v1 lets every
    // conversation stream independently.
    let conversationId: string | null = normalized.conversationId ?? null
    let wasPreCreated = false
    if (conversationId === null) {
      try {
        const created = await dispatch(
          createAgentConversation({
            instruction: trimmed,
            ...(normalized.mode === 'ingest_query_agent_only'
              ? { origin: 'ingest_query' as const }
              : {})
          })
        )
        conversationId = created.id
        wasPreCreated = true
      } catch (createErr) {
        // Pre-create failed — surface the error on the pending new-chat
        // state since there's no tab to attach it to yet.
        dispatch(setPendingDraft(trimmed))
        // The pending state doesn't have a `run` to set streamError on,
        // so we surface via console.warn; the controller hook can also
        // catch via the rejected promise if it awaits this thunk.
        console.warn(
          '[agent-orchestrator] pre-create failed',
          createErr instanceof Error ? createErr.message : createErr
        )
        throw createErr instanceof Error
          ? createErr
          : new Error('Failed to start a new conversation.')
      }
    } else {
      // Continuing an existing conversation — optimistically bump it to
      // the top of the rail so the order reflects activity immediately
      // instead of waiting for the post-stream refetch.
      dispatch(bumpAgentOrchestratorConversationToTop({ id: conversationId }))
    }

    const attachments = normalized.attachments ?? []
    const submittedClientIds = normalized.attachmentClientIds ?? []
    let attachmentsAccepted = false
    let attachmentFailuresApplied = false
    const failSubmittedAttachments = (): void => {
      if (attachmentsAccepted || attachmentFailuresApplied || submittedClientIds.length === 0) {
        return
      }
      dispatch(
        setTabStagedAttachmentStatus({
          id: conversationId,
          clientIds: submittedClientIds,
          status: 'failed',
          error: STAGED_ATTACHMENT_SEND_ERROR,
          errorCode: null,
          retryable: true,
          errorAction: 'retry'
        })
      )
    }
    const applySubmittedAttachmentFailures = (
      failures: readonly ChatAttachmentFailure[]
    ): boolean => {
      if (
        attachmentsAccepted ||
        attachmentFailuresApplied ||
        submittedClientIds.length === 0 ||
        failures.length === 0
      ) {
        return false
      }
      const aggregate = failures.find((failure) => failure.index === null)
      if (aggregate !== undefined) {
        dispatch(
          setTabStagedAttachmentStatus({
            id: conversationId,
            clientIds: submittedClientIds,
            status: 'failed',
            error: aggregate.message,
            errorCode: aggregate.code,
            retryable: aggregate.retryable,
            errorAction: aggregate.action
          })
        )
        attachmentFailuresApplied = true
        return true
      }

      const failedClientIds = new Set<string>()
      for (const failure of failures) {
        if (failure.index === null) continue
        const clientId =
          failure.clientUploadId !== undefined &&
          submittedClientIds.includes(failure.clientUploadId)
            ? failure.clientUploadId
            : submittedClientIds[failure.index]
        if (clientId === undefined) continue
        failedClientIds.add(clientId)
        dispatch(
          setTabStagedAttachmentStatus({
            id: conversationId,
            clientIds: [clientId],
            status: 'failed',
            error: failure.message,
            errorCode: failure.code,
            retryable: failure.retryable,
            errorAction: failure.action
          })
        )
      }
      if (failedClientIds.size === 0) return false
      const validClientIds = submittedClientIds.filter((clientId) => !failedClientIds.has(clientId))
      if (validClientIds.length > 0) {
        if (failures.some(isAttachmentPersistenceFailure)) {
          const claimFailure = chatAttachmentFailure('ATTACHMENT_CLAIM_FAILED', null)
          dispatch(
            setTabStagedAttachmentStatus({
              id: conversationId,
              clientIds: validClientIds,
              status: 'failed',
              error: claimFailure.message,
              errorCode: claimFailure.code,
              retryable: claimFailure.retryable,
              errorAction: claimFailure.action
            })
          )
        } else {
          dispatch(
            setTabStagedAttachmentStatus({
              id: conversationId,
              clientIds: validClientIds,
              status: 'staged'
            })
          )
        }
      }
      attachmentFailuresApplied = true
      return true
    }
    const acceptSubmittedAttachments = (): void => {
      if (attachmentsAccepted || submittedClientIds.length === 0) return
      attachmentsAccepted = true
      dispatch(
        removeTabStagedAttachments({
          id: conversationId,
          clientIds: submittedClientIds
        })
      )
      releaseStagedAttachments(submittedClientIds)
    }
    if (submittedClientIds.length > 0) {
      if (submittedClientIds.length !== attachments.length) {
        failSubmittedAttachments()
        throw new Error(STAGED_ATTACHMENT_SEND_ERROR)
      }
      const resolved = resolveStagedAttachmentFiles(submittedClientIds)
      if (resolved.missingClientIds.length > 0) {
        failSubmittedAttachments()
        throw new Error(STAGED_ATTACHMENT_SEND_ERROR)
      }
    }

    const controller = new AbortController()
    const messageId =
      globalThis.crypto?.randomUUID?.() ??
      `message-${Date.now()}-${Math.random().toString(36).slice(2)}`
    const predecessor = clientTurnTails.get(conversationId)
    let resolveCurrentTurn!: () => void
    const currentTurnDone = new Promise<void>((resolve) => {
      resolveCurrentTurn = resolve
    })
    const currentTail = (predecessor ?? Promise.resolve()).then(() => currentTurnDone)
    clientTurnTails.set(conversationId, currentTail)
    // Only surface the Queued rail when a prior turn is still live in the UI.
    // A map hit alone is not enough — the predecessor often lingers through
    // post-stream hydrate/refetch after `finishTabRun` already cleared the
    // live row, which previously flashed "Queued" on every quick follow-up.
    const queued =
      predecessor !== undefined &&
      isTabRunLive(getState().agentOrchestrator.tabs[conversationId]?.run)
    if (queued) {
      dispatch(
        enqueueTabMessage({
          id: conversationId,
          message: {
            id: messageId,
            instruction: trimmed,
            queued_at: new Date().toISOString()
          }
        })
      )
    } else {
      // Submitted without queue UI — keep the Redis poll from briefly
      // promoting this message into the Queued rail before claim/activation.
      suppressQueuedUiMessageIds.add(messageId)
    }

    let activated = false
    let turnReleased = false
    const releaseCurrentTurn = (): void => {
      if (turnReleased) return
      turnReleased = true
      resolveCurrentTurn()
    }
    let effectiveInstruction = trimmed
    /**
     * Settle a run whose local consumer was aborted — the Stop button, or a
     * close-tab disconnect. Skipped when a different controller has since
     * been registered for this conversation: that consumer's
     * `registerAbortController` is what aborted us, and the run in the store
     * is now its run, not ours.
     *
     * Whatever the run produced before it was stopped is kept. The server
     * finalizes a cancelled run as `aborted` with its partial plan and step
     * results attached, so promoting the same partial into the transcript
     * matches what a reload would show — and the composer's Continue
     * working button picks the work back up from there. Only a run that
     * never started has nothing to keep; that one hands the prompt back to
     * the composer instead.
     */
    const settleAbortedRun = (): void => {
      const owner = abortControllersByConversation.get(conversationId)
      if (owner === undefined || owner === controller) {
        if (router.didStart()) {
          dispatch(
            appendTabTurn({
              id: conversationId,
              turn: router.buildSyntheticTurn(effectiveInstruction, {
                status: 'aborted'
              })
            })
          )
        } else {
          restoreDraft(dispatch, conversationId, effectiveInstruction)
        }
        dispatch(finishTabRun({ id: conversationId }))
      }
      releaseCurrentTurn()
    }
    const bufferedEvents: OntologyAgentEvent[] = []
    const router = createOrchestratorEventHandler(dispatch, conversationId)
    const activation = (async () => {
      if (predecessor !== undefined) await predecessor
      if (queued) {
        effectiveInstruction =
          getState().agentOrchestrator.tabs[conversationId]?.queuedMessages.find(
            (message) => message.id === messageId
          )?.instruction ?? trimmed
        dispatch(removeTabQueuedMessage({ id: conversationId, messageId }))
      }
      dispatch(startTabRun({ id: conversationId, instruction: effectiveInstruction }))
      // Activated messages must stay out of the Queued rail even if Redis
      // still lists them for a moment before the lease claim completes.
      suppressQueuedUiMessageIds.add(messageId)
      registerAbortController(conversationId, controller)
      activated = true
      // Product analytics: human composer send (not resume / seed paths).
      trackUsageAction('chat_llm', 'message_sent')
      for (const event of bufferedEvents.splice(0)) router.handle(event)
    })()
    // Clear the composer immediately on submit (ChatGPT-style). We'll
    // restore the draft from `trimmed` below if the run stops / fails so
    // the user can edit + resubmit without retyping. For a freshly
    // pre-created tab the draft is already empty; for a continuing tab
    // we clear `tab.draft`; for the pending new-chat state we clear
    // `pendingDraft`.
    if (wasPreCreated) {
      dispatch(setPendingDraft(''))
    }
    dispatch(setTabDraft({ id: conversationId, draft: '' }))

    // Prefer the real tab's per-conversation choice. If the controller already
    // created an empty tab for a new conversation, move the pending composer's
    // choice onto it before constructing this first request.
    const modelState = getState().agentOrchestrator
    let modelSelection: OrchestratorModelSelection | null =
      modelState.tabs[conversationId]?.modelSelection ?? null
    if (modelSelection === null && modelState.pendingModelSelection !== null) {
      modelSelection = modelState.pendingModelSelection
      dispatch(
        setTabModelSelection({
          id: conversationId,
          selection: modelSelection
        })
      )
      dispatch(setPendingModelSelection(null))
    }

    let effortSelection: OrchestratorEffortSelection | null =
      modelState.tabs[conversationId]?.effortSelection ?? null
    if (effortSelection === null && modelState.pendingEffortSelection !== null) {
      // Auto has no thinking picker. A leftover pending pick from a previous
      // pinned-model draft must not land on an Auto conversation.
      if (modelSelection !== null) {
        effortSelection = modelState.pendingEffortSelection
        dispatch(
          setTabEffortSelection({
            id: conversationId,
            selection: effortSelection
          })
        )
      }
      dispatch(setPendingEffortSelection(null))
    }

    const requestEffort = effortForOrchestratorRequest(modelSelection, effortSelection)

    if (
      modelState.tabs[conversationId]?.composerMode == null &&
      modelState.pendingComposerMode !== null &&
      normalized.mode !== 'ingest_query_agent_only'
    ) {
      dispatch(
        setTabComposerMode({
          id: conversationId,
          mode: modelState.pendingComposerMode
        })
      )
      dispatch(setPendingComposerMode(null))
    }

    const mode: OrchestratorRunMode =
      normalized.mode === 'query_agent_only' || normalized.mode === 'ingest_query_agent_only'
        ? normalized.mode
        : 'full'
    const graphWrite = mode !== 'full' && normalized.graphWrite === true
    const ingestQueryJobId =
      mode === 'ingest_query_agent_only' ? normalized.ingestQueryJobId : undefined
    const appContext =
      normalized.appContext !== undefined && normalized.appContext.length > 0
        ? normalized.appContext
        : undefined
    const productMode = readProductMode()
    const userTimezone = readBrowserTimezone()
    const requestBody:
      | FormData
      | {
          instruction: string
          conversation_id: string
          message_id: string
          model_provider?: string
          model_id?: string
          effort?: OrchestratorEffortSelection
          mode?: 'query_agent_only' | 'ingest_query_agent_only'
          job_id?: string
          capabilities?: { graph_write: true }
          app_context?: AppContextEntry[]
          product_mode?: 'core'
          debug_mode?: true
          user_timezone: string
        } =
      attachments.length > 0
        ? buildOrchestratorFormData(
            trimmed,
            conversationId,
            attachments,
            submittedClientIds,
            modelSelection,
            effortSelection,
            messageId,
            mode,
            graphWrite,
            appContext,
            ingestQueryJobId,
            productMode,
            normalized.debugMode,
            userTimezone
          )
        : {
            instruction: trimmed,
            conversation_id: conversationId,
            message_id: messageId,
            user_timezone: userTimezone,
            ...(modelSelection !== null
              ? {
                  model_provider: modelSelection.provider,
                  model_id: modelSelection.model
                }
              : {}),
            ...(requestEffort !== null ? { effort: requestEffort } : {}),
            ...(mode !== 'full' ? { mode } : {}),
            ...(ingestQueryJobId ? { job_id: ingestQueryJobId } : {}),
            ...(graphWrite ? { capabilities: { graph_write: true as const } } : {}),
            ...(appContext !== undefined ? { app_context: appContext } : {}),
            ...(productMode === 'core' ? { product_mode: 'core' as const } : {}),
            ...(normalized.debugMode === true ? { debug_mode: true as const } : {})
          }

    try {
      await consumeSSE<OntologyAgentEvent>({
        url: `${getOntologyServiceBaseUrl()}/agent-orchestrator`,
        body: requestBody,
        signal: controller.signal,
        onEvent: (event) => {
          if (event.type === 'error' && event.attachments !== undefined) {
            applySubmittedAttachmentFailures(event.attachments)
          }
          if (event.type === 'agent_orchestrator_conversation_started') {
            acceptSubmittedAttachments()
          }
          if (activated) {
            router.handle(event)
          } else {
            bufferedEvents.push(event)
          }
        }
      })
      await activation

      // An abort only rejects while the request is still opening; once the
      // response body is streaming `consumeSSE` swallows it and resolves, so
      // a stopped run reaches us here rather than in `catch`. Without this
      // the success path would append a truncated turn and clear the run.
      if (controller.signal.aborted) {
        if (!attachmentsAccepted) failSubmittedAttachments()
        settleAbortedRun()
        return
      }

      const observedError = router.getObservedError()
      if (observedError !== null) {
        if (!attachmentsAccepted) failSubmittedAttachments()
        restoreDraft(dispatch, conversationId, effectiveInstruction)
        dispatch(failTabRun({ id: conversationId, error: observedError }))
        // Release before post-stream work so a quick follow-up is not held
        // behind hydrate/refetch (and does not flash as Queued).
        releaseCurrentTurn()
        // Decisions the user made mid-stream shouldn't be stranded by the
        // failure — fire any fully-settled plans now that nothing streams.
        void dispatch(fireSettledAgentActionPlans(conversationId))
        return
      }

      // Append the just-completed turn to the tab's transcript and clear
      // live-streaming fields. Then refresh the rail in the background
      // so the new conversation (or the bumped `updated_at` of an
      // existing one) shows up in the list.
      if (router.didStart()) {
        dispatch(
          appendTabTurn({
            id: conversationId,
            turn: router.buildSyntheticTurn(effectiveInstruction)
          })
        )
      }
      dispatch(finishTabRun({ id: conversationId }))
      // Local streaming state is idle — release the browser FIFO so the
      // next composer submit can activate immediately instead of waiting
      // on post-stream hydrate/list/conversation refetch.
      releaseCurrentTurn()

      // Post-stream sync of any cypher-write rows the SSE handler may
      // have missed (camelCase / nested field parsing quirks, late
      // /resume events landing after we closed). Idempotent — the
      // hydrate thunk merges with respect for any locally-tracked
      // terminalState the user has already produced.
      try {
        await dispatch(hydratePendingCypherWrites(conversationId))
      } catch (hydrateErr) {
        console.warn('[agent-orchestrator] post-stream cypher-write hydrate failed', hydrateErr)
      }

      // Fallback rail: plans whose last row the user settled mid-stream
      // were deferred by `maybeFireAgentActionPlanResume` (firing then
      // would open a second concurrent run on this conversation). On the
      // blocking happy path this is a no-op — the propose tool's settled
      // `tool_call_complete` always precedes stream end and retires the
      // plan's cards, so no rows remain to claim. Rows survive to here
      // only when the in-turn delivery couldn't happen (wait timed out,
      // or decisions landed against a run that had already ended).
      // Fire-and-forget — the resume turn runs its own post-stream
      // refetch sequence.
      void dispatch(fireSettledAgentActionPlans(conversationId))

      try {
        await dispatch(loadAgentConversations())
      } catch (refetchErr) {
        // Non-fatal — the rail is just stale until the next refresh.
        console.warn('[agent-orchestrator] conversation-list refetch failed', refetchErr)
      }

      // Refetch the conversation so the canonical run replaces our
      // synthetic placeholder. Does NOT change focus (multi-tab: the user
      // may have switched tabs mid-stream).
      if (router.didStart()) {
        try {
          await dispatch(loadActiveAgentConversation(conversationId))
        } catch (turnRefetchErr) {
          console.warn('[agent-orchestrator] active-conversation refetch failed', turnRefetchErr)
        }
      }

      // Re-pull the per-conversation attachment list after every started
      // turn: user uploads and agent-published drawing figures share this
      // durable registry.
      if (router.didStart()) {
        try {
          await dispatch(loadActiveAgentConversationAttachments(conversationId))
        } catch (attRefetchErr) {
          console.warn('[agent-orchestrator] attachments refetch failed', attRefetchErr)
        }
      }
    } catch (error) {
      if (controller.signal.aborted) {
        // Local AbortController fired while the request was still opening.
        // Could be the Stop button (which also POSTs /cancel) or a close-tab
        // (which doesn't). Both settle the same way — if the tab was closed
        // the dispatches are silent no-ops via the slice's defensive guards.
        if (!attachmentsAccepted) failSubmittedAttachments()
        settleAbortedRun()
        return
      }
      // Transport / pre-create / other unexpected failure.
      const appliedAttachmentFailures =
        error instanceof SSERequestError &&
        applySubmittedAttachmentFailures(error.attachmentFailures)
      if (!appliedAttachmentFailures) failSubmittedAttachments()
      restoreDraft(dispatch, conversationId, effectiveInstruction)
      dispatch(
        failTabRun({
          id: conversationId,
          error: error instanceof Error ? error.message : 'Failed to run agent orchestrator.'
        })
      )
      releaseCurrentTurn()
      // Same as the observed-error path above: don't strand mid-stream
      // decisions behind a transport failure.
      void dispatch(fireSettledAgentActionPlans(conversationId))
    } finally {
      await activation.catch(() => undefined)
      if (activated) clearAbortController(conversationId, controller)
      releaseCurrentTurn()
      suppressQueuedUiMessageIds.delete(messageId)
      void currentTail.finally(() => {
        if (clientTurnTails.get(conversationId) === currentTail) {
          clientTurnTails.delete(conversationId)
        }
      })
    }
  }
}

/**
 * Re-attach to an orchestrator run that's still in flight server-side
 * (e.g. after the user refreshed the page mid-stream, or reopened a tab
 * they had previously closed while the run kept going per decision #7).
 * Opens an SSE connection to `/agent-orchestrator/resume`; the server
 * replays every event the orchestrator emitted so far, then forwards
 * subsequent events until the run finishes.
 *
 * `instruction` is the running run's prompt — used to seed the tab's
 * `run.instruction` so the live UI surfaces the user's original prompt
 * as it streams in. A 404 from /resume means the orchestrator already
 * finished between hydration and this call; in that case we silently
 * no-op (the caller already loaded the now-complete transcript).
 *
 * No-op when a local consumer is already attached to this conversation:
 * the stream lives in the module registry, not in a React tree, so the
 * surface the user navigated away from keeps feeding the store. Attaching
 * a second consumer would abort the first, and the aborted consumer's
 * teardown clears `tab.run` — the live turn would disappear until a
 * reload.
 */
export function resumeOntologyAgentOrchestrator(args: {
  conversationId: string
  instruction: string
}) {
  return async (dispatch: AppDispatch): Promise<void> => {
    const { conversationId, instruction } = args
    if (abortControllersByConversation.has(conversationId)) return

    const controller = new AbortController()
    registerAbortController(conversationId, controller)

    // The tab should already exist (caller opened it via
    // `loadActiveAgentConversation` before triggering resume). If
    // somehow it doesn't, openTab is idempotent.
    dispatch(openTab({ id: conversationId }))
    dispatch(startTabRun({ id: conversationId, instruction }))

    const router = createOrchestratorEventHandler(dispatch, conversationId)

    try {
      await consumeSSE<OntologyAgentEvent>({
        url: `${getOntologyServiceBaseUrl()}/agent-orchestrator/resume`,
        body: { conversation_id: conversationId },
        signal: controller.signal,
        onEvent: router.handle
      })

      const observedError = router.getObservedError()
      if (observedError !== null) {
        dispatch(failTabRun({ id: conversationId, error: observedError }))
        return
      }

      // Refetch the conversation so the now-completed run replaces the
      // formerly-running entry; simpler than splicing a synthetic turn
      // into the loaded transcript. Doesn't change focus.
      try {
        await dispatch(loadActiveAgentConversation(conversationId))
      } catch (refetchErr) {
        console.warn('[agent-orchestrator] post-resume conversation refetch failed', refetchErr)
      }
      dispatch(finishTabRun({ id: conversationId }))

      try {
        await dispatch(hydratePendingCypherWrites(conversationId))
      } catch (hydrateErr) {
        console.warn('[agent-orchestrator] post-resume cypher-write hydrate failed', hydrateErr)
      }

      try {
        await dispatch(loadAgentConversations())
      } catch (refetchErr) {
        console.warn('[agent-orchestrator] conversation-list refetch failed', refetchErr)
      }
    } catch (error) {
      if (controller.signal.aborted) {
        // Leave the tab alone when a newer consumer has taken the
        // conversation over — its `registerAbortController` is what
        // aborted us, and the run in the store is now its run.
        const owner = abortControllersByConversation.get(conversationId)
        if (owner === undefined || owner === controller) {
          dispatch(finishTabRun({ id: conversationId }))
        }
        return
      }
      // 404 from /resume = orchestrator already completed before we
      // resumed; treat as a graceful no-op rather than a failure.
      const message = error instanceof Error ? error.message : String(error)
      if (message.includes('HTTP 404')) {
        dispatch(finishTabRun({ id: conversationId }))
        try {
          await dispatch(hydratePendingCypherWrites(conversationId))
        } catch (hydrateErr) {
          console.warn(
            '[agent-orchestrator] post-resume-404 cypher-write hydrate failed',
            hydrateErr
          )
        }
        // The conversation loaded with a running run, so the load-time
        // settled-plan dispatch was skipped; the run has since finished,
        // so pick up any plans that settled while it streamed.
        void dispatch(dispatchUnresumedAgentActionPlans(conversationId))
        return
      }
      dispatch(
        failTabRun({
          id: conversationId,
          error: error instanceof Error ? error.message : 'Failed to resume agent orchestrator.'
        })
      )
    } finally {
      clearAbortController(conversationId, controller)
    }
  }
}
